/**
 * Forum Tags - Preset tags for the forum system
 *
 * Tags are categorized by type and can be used to:
 * - Filter discussions
 * - Help users find content
 * - Organize topics
 *
 * Tag Types:
 * - status: Discussion state (solved, open, closed)
 * - content: Content type (tutorial, guide, question)
 * - tech: Technology/topic area
 * - difficulty: Skill level required
 * - special: Platform-specific tags
 */

// Tag type definitions
export const TAG_TYPES = {
  STATUS: 'status',
  CONTENT: 'content',
  TECH: 'tech',
  DIFFICULTY: 'difficulty',
  SPECIAL: 'special',
};

// Color presets for tags
export const TAG_COLORS = {
  // Status colors
  GREEN: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
  },
  RED: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
  },
  YELLOW: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
  },
  BLUE: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
  },
  VIOLET: {
    bg: 'bg-violet-500/20',
    text: 'text-violet-400',
    border: 'border-violet-500/30',
  },
  CYAN: {
    bg: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    border: 'border-cyan-500/30',
  },
  ORANGE: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
  },
  PINK: {
    bg: 'bg-pink-500/20',
    text: 'text-pink-400',
    border: 'border-pink-500/30',
  },
  GRAY: {
    bg: 'bg-zinc-500/20',
    text: 'text-zinc-400',
    border: 'border-zinc-500/30',
  },
  INDIGO: {
    bg: 'bg-indigo-500/20',
    text: 'text-indigo-400',
    border: 'border-indigo-500/30',
  },
  TEAL: {
    bg: 'bg-teal-500/20',
    text: 'text-teal-400',
    border: 'border-teal-500/30',
  },
  ROSE: {
    bg: 'bg-rose-500/20',
    text: 'text-rose-400',
    border: 'border-rose-500/30',
  },
};

// Hex colors for database storage
export const TAG_HEX_COLORS = {
  GREEN: '#10b981',
  RED: '#ef4444',
  YELLOW: '#f59e0b',
  BLUE: '#3b82f6',
  VIOLET: '#8b5cf6',
  CYAN: '#06b6d4',
  ORANGE: '#f97316',
  PINK: '#ec4899',
  GRAY: '#71717a',
  INDIGO: '#6366f1',
  TEAL: '#14b8a6',
  ROSE: '#f43f5e',
};

// Status tags
export const STATUS_TAGS = [
  {
    id: 'tag-solved',
    name: 'Solved',
    slug: 'solved',
    type: TAG_TYPES.STATUS,
    color: TAG_HEX_COLORS.GREEN,
    colorClasses: TAG_COLORS.GREEN,
    description: 'This discussion has been resolved',
    icon: '✓',
    isOfficial: true,
    sortOrder: 1,
  },
  {
    id: 'tag-open',
    name: 'Open',
    slug: 'open',
    type: TAG_TYPES.STATUS,
    color: TAG_HEX_COLORS.BLUE,
    colorClasses: TAG_COLORS.BLUE,
    description: 'This discussion is open for responses',
    icon: '○',
    isOfficial: true,
    sortOrder: 2,
  },
  {
    id: 'tag-closed',
    name: 'Closed',
    slug: 'closed',
    type: TAG_TYPES.STATUS,
    color: TAG_HEX_COLORS.GRAY,
    colorClasses: TAG_COLORS.GRAY,
    description: 'This discussion is closed',
    icon: '●',
    isOfficial: true,
    sortOrder: 3,
  },
  {
    id: 'tag-in-progress',
    name: 'In Progress',
    slug: 'in-progress',
    type: TAG_TYPES.STATUS,
    color: TAG_HEX_COLORS.YELLOW,
    colorClasses: TAG_COLORS.YELLOW,
    description: 'Work in progress',
    icon: '⏳',
    isOfficial: true,
    sortOrder: 4,
  },
];

// Content type tags
export const CONTENT_TAGS = [
  {
    id: 'tag-question',
    name: 'Question',
    slug: 'question',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.BLUE,
    colorClasses: TAG_COLORS.BLUE,
    description: 'A question seeking answers',
    icon: '?',
    isOfficial: true,
    sortOrder: 1,
  },
  {
    id: 'tag-discussion',
    name: 'Discussion',
    slug: 'discussion',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.VIOLET,
    colorClasses: TAG_COLORS.VIOLET,
    description: 'General discussion topic',
    icon: '💬',
    isOfficial: true,
    sortOrder: 2,
  },
  {
    id: 'tag-tutorial',
    name: 'Tutorial',
    slug: 'tutorial',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.CYAN,
    colorClasses: TAG_COLORS.CYAN,
    description: 'Step-by-step guide or tutorial',
    icon: '📚',
    isOfficial: true,
    sortOrder: 3,
  },
  {
    id: 'tag-guide',
    name: 'Guide',
    slug: 'guide',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.TEAL,
    colorClasses: TAG_COLORS.TEAL,
    description: 'Comprehensive guide',
    icon: '📖',
    isOfficial: true,
    sortOrder: 4,
  },
  {
    id: 'tag-announcement',
    name: 'Announcement',
    slug: 'announcement',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.ORANGE,
    colorClasses: TAG_COLORS.ORANGE,
    description: 'Official announcement',
    icon: '📢',
    isOfficial: true,
    sortOrder: 5,
  },
  {
    id: 'tag-showcase',
    name: 'Showcase',
    slug: 'showcase',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.PINK,
    colorClasses: TAG_COLORS.PINK,
    description: 'Show off your work',
    icon: '🎨',
    isOfficial: true,
    sortOrder: 6,
  },
  {
    id: 'tag-feedback',
    name: 'Feedback',
    slug: 'feedback',
    type: TAG_TYPES.CONTENT,
    color: TAG_HEX_COLORS.INDIGO,
    colorClasses: TAG_COLORS.INDIGO,
    description: 'Feedback or suggestions',
    icon: '💡',
    isOfficial: true,
    sortOrder: 7,
  },
];

// Tech tags (blockchain/DKG focused)
export const TECH_TAGS = [
  {
    id: 'tag-dkg',
    name: 'DKG',
    slug: 'dkg',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.VIOLET,
    colorClasses: TAG_COLORS.VIOLET,
    description: 'Decentralized Knowledge Graph topics',
    icon: '🔗',
    isOfficial: true,
    sortOrder: 1,
  },
  {
    id: 'tag-smart-contracts',
    name: 'Smart Contracts',
    slug: 'smart-contracts',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.BLUE,
    colorClasses: TAG_COLORS.BLUE,
    description: 'Smart contract development',
    icon: '📝',
    isOfficial: true,
    sortOrder: 2,
  },
  {
    id: 'tag-tokenomics',
    name: 'Tokenomics',
    slug: 'tokenomics',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.GREEN,
    colorClasses: TAG_COLORS.GREEN,
    description: 'Token economics and staking',
    icon: '💰',
    isOfficial: true,
    sortOrder: 3,
  },
  {
    id: 'tag-node-operation',
    name: 'Node Operation',
    slug: 'node-operation',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.CYAN,
    colorClasses: TAG_COLORS.CYAN,
    description: 'Running and maintaining nodes',
    icon: '🖥️',
    isOfficial: true,
    sortOrder: 4,
  },
  {
    id: 'tag-api',
    name: 'API',
    slug: 'api',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.ORANGE,
    colorClasses: TAG_COLORS.ORANGE,
    description: 'API integration and usage',
    icon: '🔌',
    isOfficial: true,
    sortOrder: 5,
  },
  {
    id: 'tag-knowledge-assets',
    name: 'Knowledge Assets',
    slug: 'knowledge-assets',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.PINK,
    colorClasses: TAG_COLORS.PINK,
    description: 'Creating and managing knowledge assets',
    icon: '🧠',
    isOfficial: true,
    sortOrder: 6,
  },
  {
    id: 'tag-sparql',
    name: 'SPARQL',
    slug: 'sparql',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.TEAL,
    colorClasses: TAG_COLORS.TEAL,
    description: 'SPARQL queries and graph traversal',
    icon: '🔍',
    isOfficial: true,
    sortOrder: 7,
  },
  {
    id: 'tag-integration',
    name: 'Integration',
    slug: 'integration',
    type: TAG_TYPES.TECH,
    color: TAG_HEX_COLORS.INDIGO,
    colorClasses: TAG_COLORS.INDIGO,
    description: 'Third-party integrations',
    icon: '🔗',
    isOfficial: true,
    sortOrder: 8,
  },
];

// Difficulty tags
export const DIFFICULTY_TAGS = [
  {
    id: 'tag-beginner',
    name: 'Beginner',
    slug: 'beginner',
    type: TAG_TYPES.DIFFICULTY,
    color: TAG_HEX_COLORS.GREEN,
    colorClasses: TAG_COLORS.GREEN,
    description: 'Suitable for beginners',
    icon: '🌱',
    isOfficial: true,
    sortOrder: 1,
  },
  {
    id: 'tag-intermediate',
    name: 'Intermediate',
    slug: 'intermediate',
    type: TAG_TYPES.DIFFICULTY,
    color: TAG_HEX_COLORS.YELLOW,
    colorClasses: TAG_COLORS.YELLOW,
    description: 'Requires some experience',
    icon: '🌿',
    isOfficial: true,
    sortOrder: 2,
  },
  {
    id: 'tag-advanced',
    name: 'Advanced',
    slug: 'advanced',
    type: TAG_TYPES.DIFFICULTY,
    color: TAG_HEX_COLORS.ORANGE,
    colorClasses: TAG_COLORS.ORANGE,
    description: 'For experienced users',
    icon: '🌳',
    isOfficial: true,
    sortOrder: 3,
  },
  {
    id: 'tag-expert',
    name: 'Expert',
    slug: 'expert',
    type: TAG_TYPES.DIFFICULTY,
    color: TAG_HEX_COLORS.RED,
    colorClasses: TAG_COLORS.RED,
    description: 'Expert-level content',
    icon: '🔥',
    isOfficial: true,
    sortOrder: 4,
  },
];

// Special/platform tags
export const SPECIAL_TAGS = [
  {
    id: 'tag-official',
    name: 'Official',
    slug: 'official',
    type: TAG_TYPES.SPECIAL,
    color: TAG_HEX_COLORS.VIOLET,
    colorClasses: TAG_COLORS.VIOLET,
    description: 'Official content from the team',
    icon: '✨',
    isOfficial: true,
    sortOrder: 1,
  },
  {
    id: 'tag-pinned',
    name: 'Pinned',
    slug: 'pinned',
    type: TAG_TYPES.SPECIAL,
    color: TAG_HEX_COLORS.ROSE,
    colorClasses: TAG_COLORS.ROSE,
    description: 'Pinned to the top',
    icon: '📌',
    isOfficial: true,
    sortOrder: 2,
  },
  {
    id: 'tag-featured',
    name: 'Featured',
    slug: 'featured',
    type: TAG_TYPES.SPECIAL,
    color: TAG_HEX_COLORS.ORANGE,
    colorClasses: TAG_COLORS.ORANGE,
    description: 'Featured content',
    icon: '⭐',
    isOfficial: true,
    sortOrder: 3,
  },
  {
    id: 'tag-hot',
    name: 'Hot',
    slug: 'hot',
    type: TAG_TYPES.SPECIAL,
    color: TAG_HEX_COLORS.RED,
    colorClasses: TAG_COLORS.RED,
    description: 'Trending discussion',
    icon: '🔥',
    isOfficial: true,
    sortOrder: 4,
  },
  {
    id: 'tag-community-pick',
    name: 'Community Pick',
    slug: 'community-pick',
    type: TAG_TYPES.SPECIAL,
    color: TAG_HEX_COLORS.PINK,
    colorClasses: TAG_COLORS.PINK,
    description: 'Selected by the community',
    icon: '💖',
    isOfficial: true,
    sortOrder: 5,
  },
];

// All tags combined
export const ALL_TAGS = [
  ...STATUS_TAGS,
  ...CONTENT_TAGS,
  ...TECH_TAGS,
  ...DIFFICULTY_TAGS,
  ...SPECIAL_TAGS,
];

// Tags grouped by type
export const TAGS_BY_TYPE = {
  [TAG_TYPES.STATUS]: STATUS_TAGS,
  [TAG_TYPES.CONTENT]: CONTENT_TAGS,
  [TAG_TYPES.TECH]: TECH_TAGS,
  [TAG_TYPES.DIFFICULTY]: DIFFICULTY_TAGS,
  [TAG_TYPES.SPECIAL]: SPECIAL_TAGS,
};

// Type labels for display
export const TAG_TYPE_LABELS = {
  [TAG_TYPES.STATUS]: 'Status',
  [TAG_TYPES.CONTENT]: 'Content Type',
  [TAG_TYPES.TECH]: 'Technology',
  [TAG_TYPES.DIFFICULTY]: 'Difficulty',
  [TAG_TYPES.SPECIAL]: 'Special',
};

// User-selectable tag types (excludes admin-only: STATUS and SPECIAL)
export const USER_SELECTABLE_TAG_TYPES = [
  TAG_TYPES.CONTENT,
  TAG_TYPES.TECH,
  TAG_TYPES.DIFFICULTY,
];

// User-selectable tags (excludes STATUS and SPECIAL - those are admin-only)
export const USER_TAGS = [
  ...CONTENT_TAGS,
  ...TECH_TAGS,
  ...DIFFICULTY_TAGS,
];

// User-selectable tags grouped by type
export const USER_TAGS_BY_TYPE = {
  [TAG_TYPES.CONTENT]: CONTENT_TAGS,
  [TAG_TYPES.TECH]: TECH_TAGS,
  [TAG_TYPES.DIFFICULTY]: DIFFICULTY_TAGS,
};

// User-selectable type labels for display
export const USER_TAG_TYPE_LABELS = {
  [TAG_TYPES.CONTENT]: 'Content Type',
  [TAG_TYPES.TECH]: 'Technology',
  [TAG_TYPES.DIFFICULTY]: 'Difficulty',
};

// Helper functions
export const getTagById = (id) => ALL_TAGS.find((tag) => tag.id === id);
export const getTagBySlug = (slug) => ALL_TAGS.find((tag) => tag.slug === slug);
export const getTagsByType = (type) => TAGS_BY_TYPE[type] || [];
export const getTagColorClasses = (tagId) => {
  const tag = getTagById(tagId);
  return tag?.colorClasses || TAG_COLORS.GRAY;
};

// For seeding the database
export const getTagsForSeed = () =>
  ALL_TAGS.map(({ colorClasses, ...tag }) => ({
    tagId: tag.id,
    name: tag.name,
    slug: tag.slug,
    type: tag.type,
    color: tag.color,
    description: tag.description,
    icon: tag.icon,
    isOfficial: tag.isOfficial,
    sortOrder: tag.sortOrder,
    isActive: true,
  }));

export default ALL_TAGS;
